// TeacherHomeScreen.js
// Home redesign (Option A, 2026-09-16): built from home/teacher modules and
// useTeacherHomeData. See docs/superpowers/specs/2026-09-16-home-redesign.md §1, §6, §11.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { View, ScrollView, StyleSheet, useWindowDimensions } from "react-native";

import { HOME, homeColors } from "../components/home/homeTheme";
import { STEM_MISSIONS, SUMMER_CAMP_DAYS } from "../components/home/launcherCatalog";
import useTeacherHomeData from "../components/home/hooks/useTeacherHomeData";

import HomeRowGrid from "../components/home/primitives/HomeRowGrid";
import GreetingBanner from "../components/home/GreetingBanner";
import ClassSwitcher from "../components/home/teacher/ClassSwitcher";
import AttentionList from "../components/home/teacher/AttentionList";
import TeacherWeekStrip from "../components/home/teacher/TeacherWeekStrip";
import NeedsReteachingList from "../components/home/teacher/NeedsReteachingList";
import EngagementDistribution from "../components/home/teacher/EngagementDistribution";
import ToReviewList from "../components/home/teacher/ToReviewList";
import QuickActions from "../components/home/teacher/QuickActions";
import SetupChecklist from "../components/home/teacher/SetupChecklist";
import ExploreSection from "../components/home/student/ExploreSection";
import PreviewAsAside from "../components/home/teacher/PreviewAsAside";

import { TeacherStatsStep, TeacherQuickActionsStep } from "../components/WalkthroughSteps";

const identity = (s) => s;
const NOOP = () => {};
const SESSION_STORAGE_KEY = "homeClassId";
const DESKTOP_BREAKPOINT = 1024;
const WEEKS_DEFAULT = 4;

const readStoredClassId = () => {
  if (typeof window === "undefined" || !window.sessionStorage) return null;
  try {
    return window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const writeStoredClassId = (classId) => {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, String(classId));
  } catch (e) {
    // sessionStorage unavailable (privacy mode, native, etc.) - ignore.
  }
};

// TeacherHomeScreen - "what do I do next?" home layout. Desktop (>=1024px):
// [AttentionList 2fr | TeacherWeekStrip 1fr] (or SetupChecklist when the
// teacher has no classes), [ToReviewList | QuickActions], ExploreSection.
// Below 1024px: single column, same order.
const TeacherHomeScreen = ({
  userName = "Teacher",
  userEmail = null,
  authToken = null,
  classes = [],
  onCreateTest,
  onAddStudent,
  onViewKnowledgeInsights,
  onOpenJourney,
  showJourneyLauncher = false,
  showStemMissionLauncher = false,
  // New attention-list / to-review callbacks (additive, default no-ops)
  onOpenTest = NOOP,
  onGradeTest = NOOP,
  onEditTest = NOOP,
  onOpenMastery = NOOP,
  onOpenTopic = NOOP,
  onMessageStudent = NOOP,
  onOpenDashboard = NOOP,
  onViewStudentHome = NOOP,
  // Lesson in a Box
  lessonPlans = [],
  lessonPlansProgress = {},
  lessonPlansLoading = false,
  lessonPlansError = null,
  onOpenLessonPlan,
  onOpenLessonPlans,
  // Preview-as-student (Explore aside)
  selectedJourneyStudentEmail = "",
  onSelectedJourneyStudentEmailChange = null,
  isDarkMode = false,
  t = identity,
  lang,
}) => {
  const { width } = useWindowDimensions();
  const isDesktop = width >= DESKTOP_BREAKPOINT;
  const colors = homeColors(isDarkMode);

  const [selectedClassId, setSelectedClassId] = useState(null);

  // Default to the first class, restoring a prior selection from
  // sessionStorage (web only) when it still matches one of this teacher's classes.
  useEffect(() => {
    if (!Array.isArray(classes) || classes.length === 0) {
      setSelectedClassId(null);
      return;
    }
    const validIds = classes.map((cls) => String(cls.class_id));
    setSelectedClassId((prev) => {
      if (prev != null && validIds.includes(String(prev))) return prev;
      const stored = readStoredClassId();
      if (stored && validIds.includes(String(stored))) return stored;
      return classes[0].class_id;
    });
  }, [classes]);

  const handleClassChange = (classId) => {
    setSelectedClassId(classId);
    writeStoredClassId(classId);
  };

  const handlers = useMemo(
    () => ({
      open_test: onOpenTest,
      open_mastery: onOpenMastery,
      open_topic: onOpenTopic,
      // WP6: hand the host the student + current class so it can open the
      // message modal (name comes from the loaded roster, when present).
      message_student: (email) => {
        const match = (studentsRef.current || []).find(
          (s) => String(s?.email || "").toLowerCase() === String(email || "").toLowerCase()
        );
        onMessageStudent(email, { name: match?.name, classId: selectedClassIdRef.current });
      },
      grade_test: onGradeTest,
    }),
    [onOpenTest, onOpenMastery, onOpenTopic, onMessageStudent, onGradeTest]
  );

  const studentsRef = useRef([]);
  const selectedClassIdRef = useRef(null);
  selectedClassIdRef.current = selectedClassId;

  const { students, attention, needsReteaching, weekStats, toReview, window: dataWindow, loading } =
    useTeacherHomeData({ userEmail, authToken, classId: selectedClassId, weeks: WEEKS_DEFAULT, handlers, t });
  studentsRef.current = students;

  const weeksLabel = dataWindow?.weeks ?? WEEKS_DEFAULT;

  // ClassSwitcher only shows "· {n} students" when the class object carries a
  // student_count; attach it for the selected class from the hook's students
  // (class-scoped), since /classes/mine has no per-class count.
  const classesWithCount = useMemo(
    () =>
      (Array.isArray(classes) ? classes : []).map((cls) =>
        String(cls.class_id) === String(selectedClassId) ? { ...cls, student_count: students.length } : cls
      ),
    [classes, selectedClassId, students.length]
  );

  // Reset the "preview as" student when they fall out of the current class's roster.
  useEffect(() => {
    if (!Array.isArray(students) || students.length === 0) {
      if (selectedJourneyStudentEmail) onSelectedJourneyStudentEmailChange?.("");
      return;
    }
    if (!students.some((s) => s?.email === selectedJourneyStudentEmail) && selectedJourneyStudentEmail) {
      onSelectedJourneyStudentEmailChange?.("");
    }
  }, [students, selectedJourneyStudentEmail, onSelectedJourneyStudentEmailChange]);

  const selectedJourneyStudent = useMemo(
    () => students.find((s) => s?.email === selectedJourneyStudentEmail) || null,
    [students, selectedJourneyStudentEmail]
  );

  const handleOpenJourney = (journeyId) => onOpenJourney?.(journeyId, selectedJourneyStudent);

  const showLessonFinder = !!onOpenLessonPlan;
  const lessonFinderProps = {
    lessons: lessonPlans,
    progressByLesson: lessonPlansProgress,
    loading: lessonPlansLoading,
    error: lessonPlansError,
    onOpenLesson: onOpenLessonPlan,
    subtitle: t("Everything you need to run the class, in order."),
  };

  // `done` is hardcoded false: this checklist only renders in the
  // zero-class state below (classes.length === 0), where
  // useTeacherHomeData returns early (classId == null) and never fetches
  // the data a real completion signal would need. Not an oversight --
  // there is nothing to compute `done` from here.
  const setupSteps = [
    { label: t("Add students"), done: false, onPress: onAddStudent },
    { label: t("Create a test"), done: false, onPress: onCreateTest },
    ...(onOpenLessonPlans ? [{ label: t("Plan a lesson"), done: false, onPress: onOpenLessonPlans }] : []),
  ];

  // ToReviewList renders null when there is nothing to review; precompute
  // that here (rather than always passing the element) so the desktop
  // HomeRowGrid below can drop the empty column instead of reserving its
  // flex share next to a null render. QuickActions never renders null.
  const toReviewNode =
    toReview.length > 0 ? (
      <ToReviewList
        items={toReview}
        onGradeTest={onGradeTest}
        onEditTest={onEditTest}
        onViewAll={onCreateTest}
        isDarkMode={isDarkMode}
        t={t}
      />
    ) : null;

  // "See the student home" used to live here too, but it is independent of
  // the journeys this aside scopes (it just flips the dashboard view, using
  // the teacher's own email rather than the selected student), so it now
  // lives in Quick actions instead -- see QuickActions' onViewStudentHome.
  const previewAside = (
    <PreviewAsAside
      students={students}
      selectedJourneyStudentEmail={selectedJourneyStudentEmail}
      onSelectedJourneyStudentEmailChange={onSelectedJourneyStudentEmailChange}
      isDarkMode={isDarkMode}
      t={t}
    />
  );

  // Precomputed once, reused in both layout branches below (toReviewNode's
  // pattern) -- unlike that node these never resolve to null; each card
  // shows its own HomeEmpty state instead of disappearing.
  const needsReteachingNode = (
    <NeedsReteachingList
      items={needsReteaching}
      loading={loading}
      onOpenTopic={onOpenTopic}
      isDarkMode={isDarkMode}
      t={t}
    />
  );
  const engagementNode = (
    <EngagementDistribution
      students={students}
      loading={loading}
      isDarkMode={isDarkMode}
      t={t}
    />
  );

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: colors.background }]}
      contentContainerStyle={styles.scrollContent}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.content}>
        <View style={styles.topRow} testID="teacher-home-topline">
          <GreetingBanner userName={userName} isDarkMode={isDarkMode} t={t} lang={lang} />
          <ClassSwitcher
            classes={classesWithCount}
            selectedClassId={selectedClassId}
            onChange={handleClassChange}
            isDarkMode={isDarkMode}
            t={t}
          />
        </View>

        {classes.length === 0 ? (
          <View style={styles.block}>
            <SetupChecklist steps={setupSteps} isDarkMode={isDarkMode} t={t} />
          </View>
        ) : isDesktop ? (
          <HomeRowGrid
            testID="teacher-home-attention-row"
            style={styles.block}
            columns={[
              {
                node: (
                  <AttentionList
                    items={attention}
                    loading={loading}
                    onOpenDashboard={onOpenDashboard}
                    isDarkMode={isDarkMode}
                    t={t}
                  />
                ),
                flex: 2,
              },
              {
                node: (
                  <TeacherStatsStep>
                    <TeacherWeekStrip
                      weekStats={weekStats}
                      weeks={weeksLabel}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </TeacherStatsStep>
                ),
                flex: 1,
              },
            ]}
          />
        ) : (
          <>
            <View style={styles.block}>
              <AttentionList
                items={attention}
                loading={loading}
                onOpenDashboard={onOpenDashboard}
                isDarkMode={isDarkMode}
                t={t}
              />
            </View>
            <TeacherStatsStep style={styles.block}>
              <TeacherWeekStrip
                weekStats={weekStats}
                weeks={weeksLabel}
                isDarkMode={isDarkMode}
                t={t}
              />
            </TeacherStatsStep>
          </>
        )}

        {isDesktop ? (
          <HomeRowGrid
            testID="teacher-home-analytics-row"
            style={styles.block}
            columns={[
              { node: needsReteachingNode, flex: 1 },
              { node: engagementNode, flex: 1 },
            ]}
          />
        ) : (
          <>
            <View style={styles.block}>{needsReteachingNode}</View>
            <View style={styles.block}>{engagementNode}</View>
          </>
        )}

        {isDesktop ? (
          <HomeRowGrid
            testID="teacher-home-to-review-row"
            style={styles.block}
            columns={[
              { node: toReviewNode, flex: 1 },
              {
                node: (
                  <TeacherQuickActionsStep>
                    <QuickActions
                      showLessonFinder={showLessonFinder}
                      lessonFinderProps={lessonFinderProps}
                      onCreateTest={onCreateTest}
                      onAddStudent={onAddStudent}
                      onViewKnowledgeInsights={onViewKnowledgeInsights}
                      onViewStudentHome={onViewStudentHome}
                      isDarkMode={isDarkMode}
                      t={t}
                    />
                  </TeacherQuickActionsStep>
                ),
                flex: 1,
              },
            ]}
          />
        ) : (
          <>
            <View style={styles.block}>{toReviewNode}</View>
            <TeacherQuickActionsStep style={styles.block}>
              <QuickActions
                showLessonFinder={showLessonFinder}
                lessonFinderProps={lessonFinderProps}
                onCreateTest={onCreateTest}
                onAddStudent={onAddStudent}
                onViewKnowledgeInsights={onViewKnowledgeInsights}
                onViewStudentHome={onViewStudentHome}
                isDarkMode={isDarkMode}
                t={t}
              />
            </TeacherQuickActionsStep>
          </>
        )}

        <ExploreSection
          showLessonFinder={false}
          showMissions={showStemMissionLauncher}
          showJourneys={showJourneyLauncher}
          onOpenJourney={handleOpenJourney}
          missionCatalog={STEM_MISSIONS}
          dayCatalog={SUMMER_CAMP_DAYS}
          aside={previewAside}
          headerZIndex={20}
          headerTestID="teacher-home-explore-header"
          studentEmail={selectedJourneyStudentEmail || userEmail}
          authToken={authToken}
          isDarkMode={isDarkMode}
          t={t}
        />
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  scrollContent: { flexGrow: 1, paddingBottom: 24 },
  content: { padding: 24 },
  topRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: HOME.gap,
    marginBottom: HOME.gap,
    position: "relative",
    zIndex: 30,
  },
  block: { marginBottom: HOME.gap },
});

export default TeacherHomeScreen;
